import type { TOC } from '@ember/component/template-only';
import { get } from '@ember/helper';
import {
  PrismBadge,
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismIconBrand,
  PrismLoadingBar,
  PrismTabsContainer,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import TrophyModal from 'lumen/components/general/roi-trophies/trophy-modal';
import HotelChannelPricingLegend from 'lumen/components/hotel/channel-pricing/legend';
import HotelChannelPricingModalsCompareChannelsPrices from 'lumen/components/hotel/channel-pricing/modals/compare-channels-prices';
import HotelChannelPricingTablesCellsDayTableThDayTableValueCell from 'lumen/components/hotel/channel-pricing/tables/cells/day-table/th-day-table-value-cell';
import HotelChannelPricingTablesChannelsTable from 'lumen/components/hotel/channel-pricing/tables/channels-table';
import HotelChannelPricingTablesDayBar from 'lumen/components/hotel/channel-pricing/tables/day-bar';
import HotelChannelPricingTablesMinorChannelsTable from 'lumen/components/hotel/channel-pricing/tables/minor-channels-table';
import HotelChannelPricingTablesPmsTable from 'lumen/components/hotel/channel-pricing/tables/pms-table';
import HotelChannelPricingTooltipsChannelOffsetTooltip from 'lumen/components/hotel/channel-pricing/tooltips/channel-offset-tooltip';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelChannelPricingController from 'lumen/controllers/hotel/channel-pricing';

interface HotelChannelPricingSignature {
  Args: {
    controller: HotelChannelPricingController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row justify-between">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          <GeneralDatepickersDatepickerRange
            @displayEvents={{false}}
            @maxDate={{@controller.maxDateMoment}}
            @minDate={{@controller.minDateMoment}}
            {{! @glint-expect-error: Incorrect type }}
            @range={{@controller.dateRange}}
            @rangeChanged={{@controller.updateSelectedRange}}
            @selectStandardRange={{true}}
            @standardRange="week"
          />
        </div>
        <div class="flex gap-200">
          {{#if @controller.hasChannelManager}}
            <div class="flex items-center justify-start">
              <PrismButton
                @buttonType="secondary"
                @iconAfter="link-out"
                @label={{t "channel_pricing.my_channels"}}
                @onClick={{@controller.openChannelManager}}
                class="ic-product-tour-57219200-btn-cm-link-out"
              />
            </div>
          {{/if}}
          <HotelChannelPricingLegend />
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    <div class="day-bar-wrapper">
      <div class="content-wrapper ic-product-tour-55124760-sd-day-bar-wrapper">
        <HotelChannelPricingTablesDayBar
          @data={{@controller.periods}}
          @isLoading={{@controller.isDayBarDataLoading}}
          @range={{@controller.dateRange}}
          @thComponent={{HotelChannelPricingTablesCellsDayTableThDayTableValueCell}}
        />
      </div>
    </div>

    <div class="content-wrapper main-content--new-layout">
      {{#if @controller.arePageRequestsLoading}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{172}} />
      {{/if}}
      <div class="mt-400">
        <HotelChannelPricingTablesPmsTable
          @caratesData={{@controller.ownHotelCarates}}
          @daysWithActivePricePush={{@controller.daysWithActivePricePush}}
          @daysWithFailedPricePush={{@controller.daysWithFailedPricePush}}
          @daysWithFailedRestrictionPush={{@controller.daysWithFailedHotelLevelRestrictionPush}}
          @isLoading={{@controller.arePmsTableDataRequestsLoading}}
          @periodsData={{@controller.periods}}
        />
      </div>

      <div class="mt-400 mb-100 flex items-center justify-between">
        <h4>
          {{t "channel_pricing.header.channels"}}
        </h4>

        {{#if @controller.showCompareChannelPrices}}
          <div class="flex gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{@controller.isCompareChannelPricesButtonDisabled}}
              @label={{t "channel_pricing.compare_channel_prices"}}
              @onClick={{@controller.openCompareChannelPricesModal}}
              class="compare-channel-prices-button relative"
            >
              {{! SMART DISTRIBUTION TODO: Remove this & CSS class if we don't revisit the notification bubble logic}}
              {{! <div
                  class="compare-channel-prices-button__notification-bubble absolute"
                /> }}
            </PrismButton>
            <PrismButton
              @isDisabled={{@controller.areChannelsTableDataRequestsLoading}}
              @onClick={{@controller.startLiveupdate}}
              class="ic-product-tour-54444680-liveshop-button"
            >
              {{#if @controller.liveupdate.areTasksRunning}}
                <PrismIcon @color="inherit" @name="sync" class="spinning" />
              {{else}}
                <PrismIcon @color="inherit" @name="sync" />
                <PrismTooltip
                  @side="bottom"
                  @text={{@controller.liveupdateButtonTooltipText}}
                />
              {{/if}}
            </PrismButton>
          </div>
        {{/if}}
      </div>

      <section class="channel-pricing-channels-tab-container">
        <PrismTabsContainer
          @activeTabId={{@controller.activeChannelTabIdWithInitialValue}}
          @onSelectTab={{@controller.onSelectChannelTab}}
          @visibleTabIds={{@controller.visibleChannelTabs.tabIds}}
          as |TabsContainer|
        >
          <PrismCard @contentLayout="noPadding" class="h-full">
            <:content>
              <TabsContainer.header
                class="ic-product-tour-49521458-sd-channel-table-tabs-header"
              >
                <:default as |Header|>
                  {{#each @controller.visibleChannelTabs.tabData as |tabData|}}
                    <Header.tab @id={{tabData.id}}>
                      <div class="flex items-center gap-100">
                        {{#if tabData.iconName}}
                          <PrismIconBrand @name={{tabData.iconName}} />
                        {{/if}}
                        {{tabData.label}}
                        {{#if tabData.numberOfRecommendations}}
                          <PrismBadge
                            @label={{tabData.numberOfRecommendations}}
                            @type="intelligence"
                          />
                        {{/if}}
                      </div>
                      {{#unless
                        (eq tabData.id @controller.CHANNEL_TAB_OTHERS_ID)
                      }}
                        <HotelChannelPricingTooltipsChannelOffsetTooltip
                          @label={{tabData.label}}
                          @offset={{get
                            @controller.channelOffsetsByChannel
                            tabData.id
                          }}
                        />
                      {{/unless}}
                    </Header.tab>
                  {{/each}}
                </:default>
              </TabsContainer.header>

              <TabsContainer.content>
                {{#if
                  (eq
                    @controller.activeChannelTabIdWithInitialValue
                    @controller.CHANNEL_TAB_OTHERS_ID
                  )
                }}
                  <HotelChannelPricingTablesMinorChannelsTable
                    @channelOffsetsByChannel={{@controller.channelOffsetsByChannel}}
                    @data={{@controller.periods}}
                    @daysWithActivePricePush={{@controller.daysWithActivePricePush}}
                    @daysWithFailedPricePush={{@controller.daysWithFailedPricePush}}
                    @daysWithFailedRestrictionPush={{@controller.daysWithFailedMinorChannelLevelRestrictionPush}}
                    @isLoading={{@controller.areChannelsTableDataRequestsLoading}}
                    @minorChannels={{@controller.minorChannels}}
                  />
                {{else}}
                  <HotelChannelPricingTablesChannelsTable
                    @data={{@controller.periods}}
                    @daysWithActivePricePush={{@controller.daysWithActivePricePush}}
                    @daysWithFailedPricePush={{@controller.daysWithFailedPricePush}}
                    @daysWithFailedRestrictionPush={{@controller.daysWithFailedChannelLevelRestrictionPush}}
                    @isLoading={{@controller.areChannelsTableDataRequestsLoading}}
                    @selectedChannelId={{@controller.activeChannelTabIdWithInitialValue}}
                  />
                {{/if}}
              </TabsContainer.content>
            </:content>
          </PrismCard>
        </PrismTabsContainer>
      </section>
    </div>
  </GeneralPageMain>
  {{#if
    (and
      @controller.showCompareChannelPrices
      @controller.isCompareChannelPricesModalOpen
    )
  }}
    <HotelChannelPricingModalsCompareChannelsPrices
      @channelsWithDiscount={{@controller.channelsDiscountData.channelsWithDiscount}}
      @channelsWithMatchingOrHigherPrice={{@controller.channelsDiscountData.channelsWithMatchingOrHigherPrice}}
      @closeCompareChannelPricesModal={{@controller.closeCompareChannelPricesModal}}
      @fromDateMoment={{@controller.fromDateMoment}}
      @latestExtractDatetime={{@controller.channelsDiscountData.latestExtractDatetime}}
    />
  {{/if}}

  {{#if
    (and
      @controller.access.hasTrophyHighlights
      @controller.roiTrophies.pricePushMilestone
    )
  }}
    <TrophyModal
      @activeTrophy={{@controller.roiTrophies.pricePushTrophy}}
      @onDismiss={{@controller.roiTrophies.dismissPricePushMilestone}}
    />
  {{/if}}
</template> satisfies TOC<HotelChannelPricingSignature>;
